/**
 * @Description 审批详情抽屉-底部审批按钮
 */
import { FC, useState, useRef } from 'react';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2Operate from '@/common/components/Others/V2Operate';
import ApprovalDialog from './components/ApprovalDialog';
import PassDialog from './components/PassDialog';
import ApprovalRebootDialog from '@/common/components/business/ExpandStore/ApprovalRebootDialog';
import Rebuttal from '@/common/components/business/ExpandStore/Rebuttal';
import AddApprover from './components/AddApprover';
import TransferDialog from './components/TransferDialog';
import { useMethods } from '@lhb/hook';
import {
  counterSign,
  denyApproval,
  rebutApproval,
  passApproval,
  revokeApproval,
  transfer,
} from '@/common/api/expandStore/approveworkbench';
import styles from './index.module.less';
import { ApprovalType } from '@/common/components/business/ExpandStore/ts-config';

interface FooterProps {
  id?: number; // 审批id
  aprDetail: any;
  showPermissions: any[];
  canEditForm: boolean; // 是否可以编辑
  taskRef: any; // 任务详情ref
  chanceFormRef: any;
  handleEdit?: Function; // 点击编辑按钮
  refreshAndClose?: Function; // 刷新并关闭
}

const Footer: FC<FooterProps> = ({
  id,
  aprDetail,
  showPermissions,
  canEditForm,
  taskRef,
  chanceFormRef,
  handleEdit,
  refreshAndClose,
}) => {
  const [passExtraParams, setPassExtraParams] = useState<any>({}); // 通过审批时的附加参数，开发异动通过时有（负责人、分公司）、机会点审批通过时有propertyValues
  const [passVisible, setPassVisible] = useState<boolean>(false); // 通过框
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false); // 拒绝/驳回框
  const [rebootVisible, setRebootVisible] = useState<boolean>(false); // 重新发起框
  const [rebuttalModal, setRebuttalModal] = useState<any>({ // 驳回弹窗
    open: false,
    data: null
  });
  const [approvalEvent, setApprovalEvent] = useState('');
  const [showAddApprover, setShowAddApprover] = useState<any>(false); // 会签弹窗
  const [showTransfer, setShowTransfer] = useState<any>(false); // 转交弹窗
  const lockRef = useRef(false);

  const methods = useMethods({
    // 通过审批
    handleApprove(val) {
      const { type } = aprDetail;
      if (canEditForm) {
        // 调用保存机会点，成功后再发起审批

        // saveHandle入参分别为needCheck，成功回调
        // 最终方法saveHandle在 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx
        setPassExtraParams({});
        (chanceFormRef?.current as any)?.saveChance(true, (propertyValues: any) => {
          setPassExtraParams(propertyValues ? { propertyValues } : {});
          this.approveInner(val);
        });
      } else if (type === ApprovalType.TaskChange) {
        // 开发异动，检查并设置特定参数后发起审批
        setPassExtraParams({});
        (taskRef?.current as any).approveTaskChange((params: any) => {
          // console.log('通过异动检查', params, val);
          setPassExtraParams(params || {});
          this.approveInner(val);
        });
      } else {
        this.approveInner(val);
      }
    },
    // 通过审批内层处理
    approveInner(val) {
      setApprovalEvent(val.event);
      setPassVisible(true);
    },
    // 拒绝
    handleReject(val) {
      setApprovalEvent(val.event);
      setApprovalVisible(true);
    },
    // 驳回
    handleRebut(val) {
      setApprovalEvent(val.event);
      const { nodes, nodeCode } = aprDetail;
      // 判断运营后台的驳回配置，如果是允许用户自由驳回节点，使用setRebuttalModal，否则使用之前的驳回弹窗，驳回到指定节点
      const targetNode = nodes?.find((nodeItem: any) => nodeItem.nodeCode === nodeCode);
      if (targetNode) {
        const { alternateRejectNode } = targetNode;
        alternateRejectNode ? setRebuttalModal({
          open: true,
          data: {
            id: aprDetail.id,
            nodeCode: aprDetail.nodeCode,
          }
        }) : setApprovalVisible(true);
        return;
      }
      // 默认
      setApprovalVisible(true);
    },
    rebuttalModalClose() {
      setRebuttalModal({
        open: false,
        data: null
      });
    },
    rebuttalModalSuccess() {
      refreshAndClose?.();
    },
    // 转交
    handleTransfer() {
      setShowTransfer(true);
    },
    // 加签
    handleCountersign() {
      setShowAddApprover(true);
    },
    // 编辑
    handleEdit() {
      handleEdit?.();
    },
    // 撤回
    handleWithdraw() {
      if (lockRef.current) return;
      lockRef.current = true;
      const { id, type, typeValue } = aprDetail;
      const params = {
        id: id,
        relationType: type,
        typeValue: typeValue,
      };
      revokeApproval(params)
        .then(() => {
          V2Message.success('撤销成功');
          refreshAndClose?.();
        })
        .finally(() => {
          lockRef.current = false;
        });
    },
    // 重新提交
    handleReboot() {
      setRebootVisible(true);
    },
    /**
     * @description 确认执行审批 通过
     */
    async confirmPassDialog(reason: string, extraParams = {}) {
      if (lockRef.current) return;
      lockRef.current = true;

      const params = {
        id: aprDetail.id,
        reason: reason,
        nodeCode: aprDetail.nodeCode,
        ...extraParams,
        ...passExtraParams, // 通过审批时的附加参数
      };
      passApproval(params).then((res) => {
        if (res) {
          V2Message.success('审批成功');
          refreshAndClose?.();
        }
      }).finally(() => {
        lockRef.current = false;
      });
    },
    /**
     * @description 确认执行审批 拒绝/驳回
     */
    confirmApprovalDialog(event: string, reason: string) {
      if (lockRef.current) return;
      lockRef.current = true;

      let fetchMethod = denyApproval;
      switch (event) {
        // 拒绝
        case 'reject':
          fetchMethod = denyApproval; // 拒绝
          break;
        case 'rebut':
          fetchMethod = rebutApproval; // 驳回
          break;
      }
      const params = {
        id: aprDetail.id,
        reason: reason,
        nodeCode: aprDetail.nodeCode,
      };
      fetchMethod(params).then((res) => {
        if (res) {
          V2Message.success('审批成功');
          refreshAndClose?.();
        }
      }).finally(() => {
        lockRef.current = false;
      });
    },
    async handleAddApprover(val) {
      const res = await counterSign({
        id,
        nodeCode: aprDetail?.nodeCode,
        countersignIds: val?.approver,
        countersignType: val?.type,
        reason: val?.remark,
      });
      if (res) {
        V2Message.success('加签成功');
        refreshAndClose?.();
      }
    },
    async handleTransferEvent(val) {
      const curNode = aprDetail?.nodes.filter(item => {
        item.nodeCode = aprDetail?.nodeCode;
      });
      const nodeId = curNode[0]?.nodeId;
      const res = await transfer({
        id,
        nodeId,
        nodeCode: aprDetail?.nodeCode,
        transferTo: val?.approver,
      });
      if (res) {
        V2Message.success('转交成功');
        refreshAndClose?.();
      }
    },
  });

  return (
    <div className={styles.footerCon}>
      <V2Operate
        operateList={showPermissions?.map(item => ({
          ...item,
          type: 'primary',
        }))}
        showBtnCount={aprDetail.permissions.length}
        onClick={btn => methods[btn.func](btn)}
      />
      {/* 通过弹窗 */}
      <PassDialog
        visible={passVisible}
        setVisible={setPassVisible}
        aprDetail={aprDetail}
        onSubmit={methods.confirmPassDialog}
      />
      {/* 拒绝/驳回弹窗 */}
      <ApprovalDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        event={approvalEvent}
        aprDetail={aprDetail}
        onSubmit={methods.confirmApprovalDialog}
      />
      {/* 驳回弹窗 */}
      <Rebuttal
        modalData={rebuttalModal}
        close={methods.rebuttalModalClose}
        success={methods.rebuttalModalSuccess}
      />
      {/* 加签弹窗 */}
      <AddApprover
        visible={showAddApprover}
        setVisible={setShowAddApprover}
        onSubmit={methods.handleAddApprover}
      />
      {/* 转交弹窗 */}
      <TransferDialog
        visible={showTransfer}
        setVisible={setShowTransfer}
        onSubmit={methods.handleTransferEvent}
      />
      <ApprovalRebootDialog
        visible={rebootVisible}
        setVisible={setRebootVisible}
        approvalId={id || 0}
        relationId={aprDetail.relationId}
        type={aprDetail.type}
        typeValue={aprDetail.typeValue}
        onComplete={() => refreshAndClose?.()}
      />
    </div>
  );
};

export default Footer;
